import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Lock, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  
  const [loginData, setLoginData] = useState({
    username: '',
    password: '',
    totp_code: '',
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      navigate('/admin/dashboard');
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, loginData);
      
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        toast.info('Wprowadź kod z aplikacji Google Authenticator');
        setIsLoading(false);
        return;
      }
      
      localStorage.setItem('admin_token', response.data.token);
      localStorage.setItem('admin_username', response.data.username);
      toast.success('Zalogowano pomyślnie');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Błąd logowania', {
        description: error.response?.data?.detail || 'Nieprawidłowe dane logowania'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand flex flex-col" data-testid="admin-login-page">
      <header className="bg-white shadow-soft">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-forest hover:text-terracotta transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-body">Powrót do strony</span>
          </Link>
          <h1 className="font-heading text-2xl text-forest">Panel Administratora</h1>
          <div className="w-32" />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-soft">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-forest/10 rounded-full flex items-center justify-center mb-4">
              {requires2FA ? <ShieldCheck className="w-8 h-8 text-forest" /> : <Lock className="w-8 h-8 text-forest" />}
            </div>
            <CardTitle className="font-heading text-2xl text-forest">
              {requires2FA ? 'Weryfikacja 2FA' : 'Agromiedzierza Admin'}
            </CardTitle>
            <CardDescription className="font-body text-forest/60">
              {requires2FA ? 'Wprowadź kod z Google Authenticator' : 'Zaloguj się do panelu administracyjnego'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {!requires2FA ? (
                <>
                  <div className="space-y-2">
                    <Label className="font-body text-forest">Nazwa użytkownika</Label>
                    <Input
                      value={loginData.username}
                      onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                      required
                      className="border-sand-dark focus:border-forest rounded-none"
                      placeholder="admin"
                      data-testid="login-username-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-body text-forest">Hasło</Label>
                    <Input
                      type="password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      className="border-sand-dark focus:border-forest rounded-none"
                      placeholder="••••••••"
                      data-testid="login-password-input"
                    />
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <Label className="font-body text-forest">Kod 2FA (6 cyfr)</Label>
                  <Input
                    value={loginData.totp_code}
                    onChange={(e) => setLoginData({ ...loginData, totp_code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                    required
                    maxLength={6}
                    className="border-sand-dark focus:border-forest rounded-none text-center text-2xl tracking-[0.5em] font-mono"
                    placeholder="000000"
                    data-testid="login-2fa-input"
                  />
                  <button 
                    type="button" 
                    onClick={() => { setRequires2FA(false); setLoginData({ ...loginData, totp_code: '' }); }}
                    className="text-sm text-forest/60 hover:text-forest underline"
                  >
                    ← Wróć do logowania
                  </button>
                </div>
              )}
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-forest hover:bg-forest-light text-white rounded-none h-12 font-heading tracking-wider"
                data-testid="login-submit-btn"
              >
                {isLoading ? 'Logowanie...' : requires2FA ? 'Zweryfikuj' : 'Zaloguj się'}
              </Button>
            </form>


          </CardContent>
        </Card>
      </main>

      <footer className="py-4 text-center">
        <p className="font-body text-sm text-forest/50">
          © {new Date().getFullYear()} Agromiedzierza VIP Village
        </p>
      </footer>
    </div>
  );
};

export default AdminLoginPage;
